import { createFileRoute } from "@tanstack/react-router";
import { BackButton } from "@/components/site/BackButton";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — KAKAIYS" },
      {
        name: "description",
        content:
          "The terms and conditions that govern your use of the KAKAIYS website and the purchase of our products.",
      },
      { property: "og:title", content: "Terms of Service — KAKAIYS" },
      { property: "og:description", content: "Terms governing the use of KAKAIYS." },
      { property: "og:url", content: "https://kakaiys.com/terms" },
    ],
      links: [{ rel: "canonical", href: "https://kakaiys.com/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <article className="pt-40 pb-24 container-luxe max-w-3xl">
      <BackButton fallback="/" className="mb-8" />
      <div className="eyebrow mb-5">Maison · Legal</div>
      <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] mb-4">Terms of Service</h1>
      <p className="text-sm text-muted-foreground mb-12">Last updated: April 2026</p>

      <div className="space-y-10 text-foreground/85">
        <section>
          <p>
            These Terms of Service ("Terms") govern your access to and use of the KAKAIYS website, and the
            purchase of products from KAKAIYS ("we", "us", "our"). By accessing our site or placing an
            order, you agree to be bound by these Terms.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">1. Eligibility</h2>
          <p>
            You must be at least 18 years old, or have the consent of a parent or legal guardian, to use
            this site or make a purchase.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">2. Products &amp; pricing</h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>All prices are listed in US Dollars (USD) unless otherwise stated.</li>
            <li>We reserve the right to change prices, product descriptions and availability without prior notice.</li>
            <li>Every piece is handcrafted and may show slight variations in colour, grain and stitching — these are inherent to natural leather and not defects.</li>
            <li>We make every effort to display product colours accurately, but actual colours may vary slightly due to monitor settings.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">3. Orders &amp; acceptance</h2>
          <p>
            Your order is an offer to purchase. We reserve the right to accept or decline any order at our
            discretion, including for reasons such as suspected fraud, payment failure, or product
            unavailability. Order confirmation occurs only when we ship your item.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">4. Payment</h2>
          <p>
            Payments are processed securely by 2Checkout (Verifone), our authorised payment provider. By
            placing an order, you represent that you are authorised to use the payment method provided.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">5. Shipping &amp; returns</h2>
          <p>
            Shipping, delivery and returns are governed by our separate
            <strong> Shipping Policy</strong> and <strong>Returns &amp; Exchanges</strong> pages, which
            form part of these Terms.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">6. Intellectual property</h2>
          <p>
            All content on this site — including text, images, logos, designs and product photography —
            is the property of KAKAIYS or its licensors and is protected by copyright, trademark and
            other laws. You may not reproduce, distribute or use any content without our written consent.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">7. User accounts</h2>
          <p>
            You are responsible for maintaining the confidentiality of your account credentials and for
            all activity under your account. Notify us immediately of any unauthorised use.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">8. Prohibited use</h2>
          <p>You agree not to:</p>
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li>Use the site for any unlawful purpose</li>
            <li>Attempt to gain unauthorised access to our systems</li>
            <li>Resell our products without written authorisation</li>
            <li>Use automated tools to scrape, copy or monitor the site</li>
            <li>Submit false or misleading information</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">9. Disclaimer of warranties</h2>
          <p>
            The site and our products are provided "as is" without warranties of any kind, express or
            implied, except as required by applicable law.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">10. Craftsmanship warranty</h2>
          <p>
            Every piece carries a <strong>6-month craftsmanship warranty</strong> from the date of delivery,
            covering manufacturing defects in stitching, hardware and lining under normal use. The warranty
            excludes natural patina, water or oil staining, scratches and marks from daily wear, damage from
            misuse, improper storage, or alterations by a third party. Full details are set out in our
            Returns &amp; Exchanges policy.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">11. Chargebacks &amp; fraud</h2>
          <p>
            We reserve the right to refuse, cancel or refund any order — and to refuse future service — in
            the event of suspected fraud, payment failure, abusive behaviour, or chargebacks filed without
            first contacting our Customer Care team. Chargebacks filed in bad faith or for items outside
            our Returns Policy will be contested with full order, delivery and communication records and
            may be reported to the relevant fraud-prevention networks.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">12. Force majeure</h2>
          <p>
            We are not liable for any delay or failure to perform caused by events beyond our reasonable
            control, including but not limited to acts of God, natural disasters, war, terrorism, civil
            unrest, strikes, pandemics, customs delays, courier disruption, or government action. In such
            cases, delivery times will be extended for the duration of the event.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">13. Limitation of liability</h2>
          <p>
            To the maximum extent permitted by law, KAKAIYS shall not be liable for any indirect,
            incidental, special or consequential damages arising from your use of the site or our
            products. Our total liability shall not exceed the amount you paid for the relevant order.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">14. Governing law</h2>
          <p>
            These Terms are governed by the laws of the Islamic Republic of Pakistan. Any dispute shall be
            subject to the exclusive jurisdiction of the courts of Sialkot, Pakistan.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">15. Changes to these Terms</h2>
          <p>
            We may update these Terms from time to time. The "Last updated" date reflects the most recent
            revision. Continued use of the site after changes constitutes acceptance.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">16. Contact</h2>
          <p>
            Questions about these Terms? Contact us at
            <a className="text-gold underline-grow" href="mailto:legal@kakaiys.com"> legal@kakaiys.com</a>.
          </p>
        </section>
      </div>
    </article>
  );
}
